import type { Cycle, Phase } from "../hooks/useCycles";
import { resolveStepProgressStatus } from "./stepProgressStatus";

type Spermiogram = NonNullable<Phase["spermiograms"]>[number];

/** The spermiogram columns a lab result fills; the collection row fills none. */
const ANALYSIS_FIGURES = [
  "spermCount",
  "spermConcentration",
  "semenVolume",
  "tmsc",
  "motility",
  "progressiveMotility",
  "normalMorphology",
] as const satisfies readonly (keyof Spermiogram)[];

type AnalysisFigures = Partial<
  Pick<Spermiogram, (typeof ANALYSIS_FIGURES)[number]>
>;

type SemenWaitPhase = Pick<Phase, "type" | "startDate" | "endDate"> & {
  spermiograms?: readonly AnalysisFigures[] | null;
};

/**
 * Whether a spermiogram row carries a result. The collection step writes a row
 * with none of these, so this is what tells the analysis apart from it.
 */
export function hasAnalysisFigure(row: AnalysisFigures): boolean {
  return ANALYSIS_FIGURES.some((figure) => row[figure] != null);
}

/**
 * The sperm-freezing steps as the treatment hub stores them. The API has no
 * analysis phase, so the analysis step lives on the `fertilization` phase
 * (`STEP_TO_PHASE_TYPE` in `usePhases`).
 */
const COLLECTION = {
  stepId: "sperm_collection",
  phaseType: "sperm_collection",
};
const ANALYSIS = { stepId: "analysis", phaseType: "fertilization" };
const STEP_IDS = [COLLECTION.stepId, ANALYSIS.stepId];

function stepStatus(
  step: { stepId: string; phaseType: string },
  phases: readonly SemenWaitPhase[],
  todayIso: string,
) {
  return resolveStepProgressStatus({
    stepId: step.stepId,
    stepIds: STEP_IDS,
    phase: phases.find((phase) => phase.type === step.phaseType),
    todayIso,
  }).status;
}

/**
 * Whether a sperm-freezing patient's collection step is recorded as done.
 *
 * The analysis and freezing steps can be started in the hub without it, and
 * their phases then say nothing true about a sample, so Home asks for this
 * before it tells anyone their sample is with the lab.
 */
export function isSpermSampleCollected(params: {
  cycle: Pick<Cycle, "cycleType"> | null | undefined;
  phases: readonly SemenWaitPhase[];
  todayIso: string;
}): boolean {
  const { cycle, phases, todayIso } = params;
  if (cycle?.cycleType !== "sperm_freezing") return false;
  return stepStatus(COLLECTION, phases, todayIso) === "completed";
}

/**
 * Whether a sperm-freezing patient has given their sample and is waiting for
 * the semen analysis.
 *
 * Read from the steps the treatment hub records, through the hub's own status
 * rule, so Home and the hub agree. The wait starts when the collection step is
 * completed and ends when the analysis step is completed or skipped, figures
 * or not, or when any spermiogram on the cycle carries a result, which is where
 * a clinic files one. The row the collection step writes carries no figures,
 * so it never ends the wait on its own.
 */
export function isAwaitingSemenAnalysis(params: {
  cycle: Pick<Cycle, "cycleType" | "status"> | null | undefined;
  phases: readonly SemenWaitPhase[];
  todayIso: string;
}): boolean {
  const { cycle, phases, todayIso } = params;
  if (cycle?.cycleType !== "sperm_freezing" || cycle.status !== "open") {
    return false;
  }
  if (stepStatus(COLLECTION, phases, todayIso) !== "completed") return false;

  const analysis = stepStatus(ANALYSIS, phases, todayIso);
  if (analysis === "completed" || analysis === "skipped") return false;

  return !phases.some((phase) =>
    (phase.spermiograms ?? []).some(hasAnalysisFigure),
  );
}

/**
 * The facts Home can state about a sample at the lab, from the same steps.
 *
 * `collectedOn` is the collection step's date: completing a step stores the day
 * it happened as the phase's `startDate`. `analysisOn` is the analysis step's
 * booked date, and only while it is still ahead (or today); a date already
 * behind says nothing about when the result comes. `clinic` is the cycle's
 * provider, which the API holds as a name.
 */
export function semenAnalysisWaitFacts(params: {
  cycle: Pick<Cycle, "provider"> | null | undefined;
  phases: readonly SemenWaitPhase[];
  todayIso: string;
}): {
  collectedOn: string | null;
  analysisOn: string | null;
  clinic: string | null;
} {
  const { cycle, phases, todayIso } = params;
  const collection = phases.find(
    (phase) => phase.type === COLLECTION.phaseType,
  );
  const analysis = phases.find((phase) => phase.type === ANALYSIS.phaseType);
  const analysisIsBooked =
    stepStatus(ANALYSIS, phases, todayIso) === "scheduled" &&
    !!analysis?.startDate &&
    analysis.startDate >= todayIso;
  return {
    collectedOn:
      stepStatus(COLLECTION, phases, todayIso) === "completed"
        ? (collection?.startDate ?? null)
        : null,
    analysisOn: analysisIsBooked ? (analysis?.startDate ?? null) : null,
    clinic: cycle?.provider?.trim() || null,
  };
}
